const RAW_API_BASE = (import.meta.env.VITE_WAREHOUSE_API_BASE ?? "").trim();
const API_BASE = RAW_API_BASE.replace(/\/+$/, "");
const SUCCESS_CACHE_MS = 20_000;
const DEFAULT_TIMEOUT_MS = 8_000;

export const warehouseApiConfigured = Boolean(API_BASE);

export interface WarehouseZoneSnapshotData {
  code: string;
  kind: "main" | "auxiliary";
  row: number;
  column: number;
  observedCount: number;
  nearestOutbound?: boolean;
}

export interface WarehouseInventoryLocationPatch {
  code: string;
  stockState: "stocked" | "empty" | "unknown";
  stockQuantity: number;
  skuCount?: number;
  batchCount?: number;
  productNames?: string[];
}

interface ApiSnapshot<T> {
  version: string;
  updatedAt: string;
  source: string;
  data: T;
}

export interface WarehouseZonesSnapshot extends ApiSnapshot<WarehouseZoneSnapshotData[]> {}

export interface WarehouseInventorySnapshot extends ApiSnapshot<{
  zone: string;
  locations: WarehouseInventoryLocationPatch[];
}> {}

export interface WarehouseStationStockRow {
  location: string;
  sku?: string;
  product?: string;
  quantity?: number;
  batch?: string;
  expiresOn?: string;
}

export interface WarehouseStationStockSnapshot extends ApiSnapshot<WarehouseStationStockRow[]> {}

export interface WarehouseAssistantReply {
  message: string;
  references?: string[];
}

interface RequestOptions {
  method?: "GET" | "POST";
  body?: unknown;
  signal?: AbortSignal;
  timeoutMs?: number;
  bypassCache?: boolean;
}

interface CacheEntry<T> {
  expiresAt: number;
  value: T;
}

interface InFlightRequest<T> {
  controller: AbortController;
  promise: Promise<T>;
  waiters: Set<symbol>;
}

const successCache = new Map<string, CacheEntry<unknown>>();
const inFlight = new Map<string, InFlightRequest<unknown>>();

function requestKey(path: string, options: RequestOptions) {
  return `${options.method ?? "GET"} ${path} ${options.body ? JSON.stringify(options.body) : ""}`;
}

function abortError() {
  return new DOMException("Request aborted", "AbortError");
}

function waitForRequest<T>(
  entry: InFlightRequest<T>,
  signal?: AbortSignal,
): Promise<T> {
  const waiter = Symbol("warehouse-request");
  entry.waiters.add(waiter);
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const cleanup = () => signal?.removeEventListener("abort", onAbort);
    const settle = (callback: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      callback();
    };
    const onAbort = () => {
      entry.waiters.delete(waiter);
      if (!entry.waiters.size) entry.controller.abort();
      settle(() => reject(abortError()));
    };

    if (signal?.aborted) {
      onAbort();
      return;
    }
    signal?.addEventListener("abort", onAbort, { once: true });
    entry.promise.then(
      (value) => {
        entry.waiters.delete(waiter);
        settle(() => resolve(value));
      },
      (error) => {
        entry.waiters.delete(waiter);
        settle(() => reject(error));
      },
    );
  });
}

async function performRequest<T>(path: string, options: RequestOptions) {
  if (!warehouseApiConfigured) throw new Error("Warehouse API is not configured");
  const controller = new AbortController();
  const forwardAbort = () => controller.abort(options.signal?.reason);
  if (options.signal?.aborted) {
    forwardAbort();
  } else {
    options.signal?.addEventListener("abort", forwardAbort, { once: true });
  }
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      method: options.method ?? "GET",
      headers: {
        Accept: "application/json",
        ...(options.body ? { "Content-Type": "application/json" } : {}),
      },
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`Warehouse API returned ${response.status}`);
    }
    const payload = await response.json() as T | { success?: boolean; data?: T };
    if (
      payload
      && typeof payload === "object"
      && "success" in payload
      && "data" in payload
    ) {
      if (payload.success === false) throw new Error("Warehouse API rejected the request");
      return payload.data as T;
    }
    return payload as T;
  } finally {
    window.clearTimeout(timer);
    options.signal?.removeEventListener("abort", forwardAbort);
  }
}

async function requestJson<T>(path: string, options: RequestOptions = {}) {
  if (!warehouseApiConfigured) throw new Error("Warehouse API is not configured");
  const key = requestKey(path, options);
  if (!options.bypassCache) {
    const cached = successCache.get(key) as CacheEntry<T> | undefined;
    if (cached && cached.expiresAt > Date.now()) return cached.value;
    if (cached) successCache.delete(key);
  }

  let entry = inFlight.get(key) as InFlightRequest<T> | undefined;
  if (!entry) {
    const controller = new AbortController();
    const promise = performRequest<T>(path, { ...options, signal: controller.signal });
    entry = { controller, promise, waiters: new Set() };
    inFlight.set(key, entry as InFlightRequest<unknown>);
    promise.then(
      (value) => {
        successCache.set(key, { expiresAt: Date.now() + SUCCESS_CACHE_MS, value });
      },
      () => undefined,
    ).finally(() => {
      if (inFlight.get(key) === entry) inFlight.delete(key);
    });
  }
  return waitForRequest(entry, options.signal);
}

function zoneQuery(zone: string) {
  return `zone=${encodeURIComponent(zone.trim().toUpperCase())}`;
}

export function getWarehouseZones(signal?: AbortSignal) {
  return requestJson<WarehouseZonesSnapshot>("/warehouse/zones", { signal });
}

export function getWarehouseLayout(zone: string, signal?: AbortSignal) {
  return requestJson<unknown>(
    `/warehouse/zones/${encodeURIComponent(zone.trim().toUpperCase())}/layout`,
    { signal },
  );
}

export function getLocationInventory(code: string, signal?: AbortSignal) {
  return requestJson<WarehouseInventorySnapshot>(
    `/inventory/locations/${encodeURIComponent(code.trim().toUpperCase())}`,
    { signal },
  );
}

export function getWarehouseStock(zone: string, signal?: AbortSignal) {
  return requestJson<WarehouseInventorySnapshot>(
    `/inventory/stock?${zoneQuery(zone)}`,
    { signal, bypassCache: true },
  );
}

export function getStationStock(
  zone: string,
  aisle: string,
  signal?: AbortSignal,
) {
  return requestJson<WarehouseStationStockSnapshot>(
    `/inventory/stock?${zoneQuery(zone)}&aisle=${encodeURIComponent(aisle)}`,
    { signal, bypassCache: true },
  );
}

export function askWarehouseAssistant(
  message: string,
  context?: Record<string, unknown>,
  signal?: AbortSignal,
) {
  return requestJson<WarehouseAssistantReply>("/assistant/chat", {
    method: "POST",
    body: { message, context },
    signal,
    bypassCache: true,
  });
}
